import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import AppContext from "app/AppContext";
import { AppState } from "../../store";
import { RouteTypes } from "app/types/PageTypes";

import TopbarLayout from "./topbar/TopbarLayout";
import SubTopbar from "./sub-topbar/SubTopbar";
import Navbar from "./navbar/Navbar";
import QuickPanel from "app/main/shared-components/quick-panel/QuickPanel";
import AppMessage from "app/main/shared-components/app-message/AppMessage";

import useStyles from "./admin-layout.styles";
import useAuthority from "app/hooks/useAuthority";
import useMutationObserver from "app/hooks/useMutationObservable";

import ReportPageConfig from "app/main/report/ReportPageConfig";
import AgencyEditPageConfig from "../agency/AgencyEditPageConfig";
import AgencyReportsPageConfig from "../agency-reports/AgencyReportsPageConfig";

import { fetchAllPaymentMethods } from "../configurations/store/paymentmethods/paymentmethods-list.slice";
import { getAccount } from "../../account/store/get-account.slice";
import { ROLES } from "../../constants/roles";

const AdminLayout = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const appContext = useContext(AppContext);
  const { setPagesConfigs, pagesConfigs } = appContext;

  const { hasUserAnyAuthority } = useAuthority();

  const pagesWrapperRef = useRef<any>();
  const headerRef = useRef<any>();
  const [headerHeight, setHeaderHeight] = useState(0);

  const paymentMeth = useSelector(
    (state: AppState) => state.configurations.paymentmethods.paymentmethodsList.allPaymentMethods
  );

  const balances = useSelector((state: AppState) => state.agents.agent.agent?.balances);

  const account = useSelector((state: AppState) => state.account.account);
  const balanceResponse = account?.account?.balanceResponse;

  // compute once per render; effect depends on boolean (stable), not the function identity
  const isAgent = useMemo(() => hasUserAnyAuthority([ROLES.ROLE_AGENT]), [hasUserAnyAuthority]);

  // helper: insert/update a page config by baseRoute.path
  const upsertPageConfig = useCallback(
    (nextConfig: any) => {
      setPagesConfigs((prev: any[]) => {
        const filtered = prev.filter((c) => c.baseRoute.path !== nextConfig.baseRoute.path);
        return [nextConfig, ...filtered];
      });
    },
    [setPagesConfigs]
  );

  useEffect(() => {
    dispatch(getAccount());
  }, [dispatch]);

  useEffect(() => {
    setHeaderHeight(headerRef?.current?.clientHeight);
    dispatch(fetchAllPaymentMethods());
  }, [dispatch]);

  useEffect(() => {
    pagesWrapperRef.current?.scrollTo?.(0, 0);
  }, [history.location.pathname]);

  // Detect when height of the header changes and set the headerHeight
  useMutationObserver({
    target: headerRef,
    callback: useCallback(() => {
      setHeaderHeight(headerRef?.current?.clientHeight);
    }, []),
  });

  // Add Report pages when payment methods loaded
  useEffect(() => {
    if (paymentMeth?.length > 0) {
      upsertPageConfig(ReportPageConfig(paymentMeth));
    }
  }, [paymentMeth, upsertPageConfig]);

  // Add Agency Edit config when balances change
  useEffect(() => {
    upsertPageConfig(AgencyEditPageConfig(balances?.length ? balances : []));
  }, [balances, upsertPageConfig]);

  // Add Agency Reports config ONLY for agents
  useEffect(() => {
    if (!isAgent) return;
    upsertPageConfig(AgencyReportsPageConfig(balanceResponse?.length ? balanceResponse : []));
  }, [isAgent, balanceResponse, upsertPageConfig]);

  const pageLayoutWrapperHeight = { height: `calc(100vh - ${headerHeight}px)` };

  return (
    <div className={classes.root}>
      <div ref={headerRef}>
        <TopbarLayout />
        <SubTopbar />
      </div>

      <QuickPanel />

      <div className={classes.pageLayoutWrapper}>
        <Navbar headerHeight={headerHeight} />

        <div ref={pagesWrapperRef} className={classes.pagesWrapper} style={{ ...pageLayoutWrapperHeight }}>
          <Switch>
            {pagesConfigs.map((config: any, idx: number) => {
              if (hasUserAnyAuthority(config.baseRoute.authorities || [])) {
                return (
                  <Route
                    key={idx}
                    path={config.baseRoute.path}
                    render={() => (
                      <config.baseRoute.component settings={config.settings} key={idx + config.baseRoute.path}>
                        {config.subRoutes.map((subRoute: RouteTypes, sIdx: number) => {
                          if (hasUserAnyAuthority(subRoute.authorities || [])) {
                            return (
                              <Route
                                key={sIdx}
                                path={subRoute.path}
                                render={(otherProps) => (
                                  <subRoute.component
                                    key={sIdx + subRoute.path}
                                    settings={config.settings}
                                    {...otherProps}
                                  />
                                )}
                              />
                            );
                          }
                          return null;
                        })}
                      </config.baseRoute.component>
                    )}
                  />
                );
              }
              return null;
            })}
          </Switch>
        </div>
      </div>

      <AppMessage />
    </div>
  );
};

export default AdminLayout;
